/**
 * PreviewPage — /p/preview route loaded in the admin builder iframe.
 * Listens for ARCEE_PAGE_SCHEMA postMessage from the admin and renders
 * the sections live using DynamicPageRenderer.
 * Sends ARCEE_PREVIEW_READY once mounted so the admin knows it can post.
 */

import React, { useEffect, useState } from 'react';
import { useTheme } from '../theme/ThemeContext';
import DynamicPageRenderer from '../components/dynamic-page/DynamicPageRenderer';

export default function PreviewPage() {
  const { theme, setTheme, themesList } = useTheme();
  const [sections, setSections] = useState([]);
  const [receivedTheme, setReceivedTheme] = useState(null);

  useEffect(() => {
    // Notify the parent admin frame that we're ready
    window.parent.postMessage({ type: 'ARCEE_PREVIEW_READY' }, '*');

    const handler = (event) => {
      if (event.data?.type !== 'ARCEE_PAGE_SCHEMA') return;
      const { schema, theme: themeData } = event.data;
      if (schema?.sections) setSections(schema.sections);
      if (themeData) {
        setReceivedTheme(themeData);
        // Try to match a registered theme by name
        const match = themesList?.find(t => t.name === themeData.name);
        if (match) {
          setTheme(match);
        } else if (themeData.primary) {
          // Apply inline CSS variables directly
          const root = document.documentElement;
          if (themeData.primary) root.style.setProperty('--primary-color', themeData.primary);
          if (themeData.secondary) root.style.setProperty('--secondary-color', themeData.secondary);
          if (themeData.gradientBackground) root.style.setProperty('--gradient-bg', themeData.gradientBackground);
        }
      }
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [setTheme, themesList]);

  const activeTheme = receivedTheme || theme;

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      {sections.length === 0 ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60vh',
            color: '#94a3b8',
            gap: 12,
          }}
        >
          <i className="bi bi-layout-text-window-reverse" style={{ fontSize: 48 }} />
          <div style={{ fontWeight: 600 }}>Page preview will appear here</div>
          <div style={{ fontSize: 13 }}>Add sections in the builder and click Preview.</div>
        </div>
      ) : (
        <DynamicPageRenderer sections={sections} theme={activeTheme} />
      )}
    </div>
  );
}
